<template>
  <div class="admin-blogs-index p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Blog</h1>
        <p class="text-gray-600 mt-1">Manage blog posts</p>
      </div>
      <Link
        :href="route('posts.create')"
        class="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
      >
        <icon name="plus" class="w-5 h-5" />
        Create Post
      </Link>
    </div>

    <!-- Search -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 p-4 mb-6">
      <form @submit.prevent="search" class="flex gap-4">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search posts..."
          class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
        <button
          type="submit"
          class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Search
        </button>
      </form>
    </div>

    <!-- Posts Table -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="post in posts.data" :key="post.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap">
                <img
                  v-if="post.image"
                  :src="post.image"
                  alt=""
                  class="w-12 h-12 rounded-lg object-cover border border-gray-200"
                />
                <div v-else class="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  <icon name="file" class="w-6 h-6 text-gray-300" />
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ post.title }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {{ post.type || '—' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span
                  :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    post.isActive === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  ]"
                >
                  {{ post.isActive }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <div class="flex items-center gap-2">
                  <Link
                    :href="route('posts.edit', post.id)"
                    class="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Edit
                  </Link>
                  <button
                    @click="deletePost(post.id)"
                    class="text-red-600 hover:text-red-800 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="posts.data.length === 0">
              <td colspan="5" class="px-6 py-12 text-center">
                <div class="flex flex-col items-center gap-3">
                  <icon name="post" class="w-16 h-16 text-gray-300" />
                  <p class="text-gray-500 text-lg font-medium">No posts found</p>
                  <Link
                    :href="route('posts.create')"
                    class="text-green-600 hover:text-green-800 hover:underline"
                  >
                    Create your first post
                  </Link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="posts.links && posts.links.length > 3" class="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing {{ posts.from }} to {{ posts.to }} of {{ posts.total }} results
        </div>
        <div class="flex gap-2">
          <Link
            v-for="link in posts.links"
            :key="link.label"
            :href="link.url || '#'"
            v-html="link.label"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              link.active
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300',
              !link.url ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            ]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    posts: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: 'Blog',
    },
  },
  data() {
    return {
      searchQuery: this.filters.search || '',
    }
  },
  methods: {
    search() {
      router.get(route('posts'), { search: this.searchQuery }, {
        preserveState: true,
        preserveScroll: true,
      })
    },
    deletePost(id) {
      if (confirm('Are you sure you want to delete this post?')) {
        router.delete(route('posts.destroy', id), {
          preserveScroll: true,
          onSuccess: () => {
            if (window.$toast) {
              window.$toast.success('Post deleted successfully')
            }
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.admin-blogs-index {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>
